<script lang="ts">
  import { onMount, tick } from 'svelte';
  import type { Driver } from '../types';

  interface Props {
    drivers: Record<string, Driver>;
    selectedDriverId: string | null;
    routeDeliveries?: any[];
    selectedZona?: string;
  }

  let { drivers, selectedDriverId, routeDeliveries = [], selectedZona = '' }: Props = $props();
  let mapContainer: HTMLDivElement | undefined = $state();
  let map: any = null;
  let markers: Record<string, any> = {};
  let mapReady = $state(false);
  let deliveryMarkers: any[] = [];

  onMount(async () => {
    await tick();
    setTimeout(() => {
      initMap();
    }, 100);
  });

  function initMap() {
    const L = (window as any).L;
    if (!L) {
      console.error('Leaflet not loaded');
      return;
    }

    if (!mapContainer) {
      console.error('Map container not ready');
      return;
    }

    map = L.map(mapContainer, {
      zoomControl: true,
      attributionControl: false
    }).setView([-33.4489, -70.6693], 11);

    // Esri World Light Gray Canvas: grisáceo, sin API key.
    L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}', {
      attribution: 'Tiles © Esri',
      maxZoom: 16,
      crossOrigin: true
    }).addTo(map);

    mapReady = true;
    console.log('✅ Mapa inicializado');

    // Force resize after init
    setTimeout(() => {
      map.invalidateSize();
    }, 200);
  }

  function showDeliveryMarkers(deliveries: any[], shouldFitBounds: boolean = true) {
    const L = (window as any).L;
    
    // Remove previous delivery markers
    deliveryMarkers.forEach(marker => {
      map.removeLayer(marker);
    });
    deliveryMarkers = [];
    
    if (!deliveries || deliveries.length === 0) {
      console.log('📍 No deliveries to show');
      return;
    }
    
    // Filter valid coordinates
    const validDeliveries = deliveries.filter(d => 
      d.lat && d.lon && 
      typeof d.lat === 'number' && typeof d.lon === 'number' &&
      !isNaN(d.lat) && !isNaN(d.lon) &&
      d.lat >= -56 && d.lat <= -17 &&
      d.lon >= -80 && d.lon <= -66
    );
    
    if (validDeliveries.length === 0) {
      console.log('❌ No valid coordinates in deliveries');
      return;
    }
    
    console.log(`📍 Showing ${validDeliveries.length} delivery markers (fitBounds: ${shouldFitBounds})`);
    
    // Add markers for each delivery with status-based colors
    const bounds: Array<[number, number]> = [];
    
    validDeliveries.forEach((delivery) => {
      // Check status - use real DB values
      const status = (delivery.status || '').toUpperCase();
      const isCompleted = ['EN RECEPCION', 'EN DESCARGO', 'ENTREGADO'].includes(status);
      const isFailed = ['EN SOBRANCIA', 'EN RETENCION'].includes(status);
      
      let bgColor = 'bg-yellow-500'; // Pending - bright yellow
      let textColor = 'text-black';
      let icon = '📦';
      let statusLabel = 'Pendiente';
      
      if (isCompleted) {
        bgColor = 'bg-green-500';
        textColor = 'text-white';
        icon = '✓';
        statusLabel = 'Completada';
      } else if (isFailed) {
        bgColor = 'bg-red-500';
        textColor = 'text-white';
        icon = '✗';
        statusLabel = 'Fallida';
      }
      
      const deliveryIcon = L.divIcon({
        html: `<div style="display:flex;align-items:center;justify-content:center;width:32px;height:32px;border-radius:50%;font-weight:bold;font-size:16px;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.4);cursor:pointer;" class="${bgColor} ${textColor}">${icon}</div>`,
        iconSize: [32, 32],
        iconAnchor: [16, 16],
        className: 'delivery-marker'
      });
      
      const marker = L.marker([delivery.lat, delivery.lon], { icon: deliveryIcon }).addTo(map);
      
      // Popup with delivery info
      marker.bindPopup(`
        <div style="min-width:200px;">
          <b style="font-size:14px;">${delivery.client || 'Cliente'}</b><br>
          <span style="color:#666;font-size:12px;">OT: ${delivery.id || 'N/A'}</span><br>
          <hr style="margin:8px 0;border:none;border-top:1px solid #ddd;">
          <b>Dirección:</b><br>
          <span style="font-size:12px;">${delivery.address || 'N/A'}</span><br>
          <br>
          <b>Estado:</b> <span style="color:${isCompleted ? 'green' : isFailed ? 'red' : 'orange'};font-weight:bold;">${delivery.status || 'N/A'}</span>
        </div>
      `);
      
      // Click handler: zoom to marker
      marker.on('click', () => {
        map.setView([delivery.lat, delivery.lon], 16, {
          animate: true,
          duration: 0.5
        });
      });
      
      deliveryMarkers.push(marker);
      bounds.push([delivery.lat, delivery.lon]);
    });
    
    // Include driver location in bounds if available
    if (selectedDriverId && drivers[selectedDriverId]) {
      const driver = drivers[selectedDriverId];
      if (driver.lat && driver.lon) {
        bounds.push([driver.lat, driver.lon]);
      }
    }
    
    // Only fit map to show all markers if requested (first time showing deliveries)
    if (shouldFitBounds && bounds.length > 0) {
      console.log('🗺️ Fitting map to delivery bounds');
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }

  $effect(() => {
    if (!map || !mapReady) {
      console.log(`⚠️ MapPanel not ready: map=${!!map}, mapReady=${mapReady}`);
      return;
    }

    const L = (window as any).L;
    const driverEntries = Object.entries(drivers);
    
    let driversWithCoords = 0;
    let driversWithoutCoords = 0;
    
    // Update markers based on drivers
    driverEntries.forEach(([driver_id, driver], index) => {
      const lat = driver.lat;
      const lon = driver.lon;
      
      // Validar coordenadas válidas dentro de Chile
      const isValidCoord = lat !== null && lat !== undefined && 
        lon !== null && lon !== undefined &&
        typeof lat === 'number' && typeof lon === 'number' &&
        !isNaN(lat) && !isNaN(lon) &&
        lat !== 0 && lon !== 0 &&
        lat >= -56 && lat <= -17 &&
        lon >= -80 && lon <= -66;
      
      if (isValidCoord) {
        driversWithCoords++;
        if (markers[driver_id]) {
          markers[driver_id].setLatLng([lat, lon]);
        } else {
          // Create icon using the CXP-Car SVG from public folder
          const carIcon = L.icon({
            iconUrl: '/CXP-Car.svg',
            iconSize: [40, 40],
            iconAnchor: [20, 20],
            popupAnchor: [0, -20],
            className: 'car-marker'
          });
          
          const marker = L.marker([lat, lon], { icon: carIcon }).addTo(map);
          
          marker.bindPopup(`
            <b>${driver.name || 'Conductor'}</b><br>
            Entregas: ${driver.deliveries_count || 0}<br>
            ${driver.zona ? `Zona: ${driver.zona}` : ''}
          `);
          markers[driver_id] = marker;
          console.log(`✅ Marcador creado: ${driver.name} [${lat.toFixed(4)}, ${lon.toFixed(4)}]`);
        }

        // Show/hide markers based on selection
        if (selectedDriverId) {
          if (driver_id === selectedDriverId) {
            if (!map.hasLayer(markers[driver_id])) {
              markers[driver_id].addTo(map);
            }
            // Only open popup when marker is first created, not on every location update
          } else {
            if (map.hasLayer(markers[driver_id])) {
              map.removeLayer(markers[driver_id]);
            }
          }
        } else {
          if (!map.hasLayer(markers[driver_id])) {
            markers[driver_id].addTo(map);
          }
        }
      } else {
        driversWithoutCoords++;
        if (index < 3) {
          console.log(`❌ Coords inválidas: ${driver.name} [lat=${lat}, lon=${lon}] - Validación: null=${lat === null}, undef=${lat === undefined}, type=${typeof lat}, isNaN=${isNaN(lat)}, range=${lat >= -56 && lat <= -17}`);
        }
      }
    });
    
    // Remove markers for drivers that no longer exist
    Object.keys(markers).forEach(id => {
      if (!drivers[id]) {
        markers[id].remove();
        delete markers[id];
      }
    });
  });

  // Track previous zona to detect changes
  let previousZona = $state<string | undefined>(undefined);
  
  // Effect to center map on zone when selectedZona changes
  $effect(() => {
    // Access selectedZona to track it
    const currentZona = selectedZona;
    
    if (!map || !mapReady) return;
    
    // Only run when zona actually changes (not on every drivers update)
    if (previousZona === currentZona) return;
    previousZona = currentZona;
    
    console.log(`🗺️ Zona cambió a: "${currentZona || 'todas'}"`);
    
    // Small delay to let drivers load first
    setTimeout(() => {
      const driverEntries = Object.entries(drivers);
      if (driverEntries.length === 0) {
        console.log('⚠️ No hay conductores para centrar');
        return;
      }
      
      // Collect bounds from all visible drivers
      const bounds: Array<[number, number]> = [];
      driverEntries.forEach(([_, driver]) => {
        const lat = driver.lat;
        const lon = driver.lon;
        const isValidCoord = lat !== null && lat !== undefined && 
          lon !== null && lon !== undefined &&
          typeof lat === 'number' && typeof lon === 'number' &&
          !isNaN(lat) && !isNaN(lon) &&
          lat !== 0 && lon !== 0 &&
          lat >= -56 && lat <= -17 &&
          lon >= -80 && lon <= -66;
        
        if (isValidCoord) {
          bounds.push([lat, lon]);
        }
      });
      
      if (bounds.length > 0) {
        console.log(`🗺️ Centrando mapa con ${bounds.length} conductores`);
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 13 });
      } else {
        console.log('⚠️ No hay conductores con coordenadas válidas');
      }
    }, 500);
  });

  // Track previous deliveries to avoid unnecessary re-renders
  let previousDeliveriesLength = $state(0);
  let hasZoomedToDeliveries = $state(false);
  
  $effect(() => {
    if (!map || !mapReady) {
      console.log(`⚠️ MapPanel deliveries effect: map=${!!map}, mapReady=${mapReady}`);
      return;
    }
    
    const currentLength = routeDeliveries?.length || 0;
    console.log(`📍 Deliveries effect triggered: routeDeliveries=${currentLength}, previous=${previousDeliveriesLength}`);
    
    // Only update if deliveries actually changed
    if (currentLength === previousDeliveriesLength && currentLength > 0) {
      console.log(`📍 Deliveries unchanged, skipping update`);
      return;
    }
    
    const isFirstTimeShowingDeliveries = previousDeliveriesLength === 0 && currentLength > 0;
    previousDeliveriesLength = currentLength;
    
    // Show delivery markers when deliveries change (no route line)
    if (routeDeliveries && routeDeliveries.length > 0) {
      console.log(`📍 Mostrando ${routeDeliveries.length} entregas`);
      // Only fit bounds the first time we show deliveries
      showDeliveryMarkers(routeDeliveries, isFirstTimeShowingDeliveries);
      hasZoomedToDeliveries = true;
    } else {
      console.log(`📍 Limpiando marcadores de entregas`);
      // Clear delivery markers
      deliveryMarkers.forEach(marker => {
        map.removeLayer(marker);
      });
      deliveryMarkers = [];
      hasZoomedToDeliveries = false;
    }
  });
</script>

<div class="flex-1 flex flex-col gap-3 min-w-0 min-h-0">
  <div class="flex-1 rounded-2xl overflow-hidden border border-white/10 shadow-xl relative min-h-0">
    <div bind:this={mapContainer} class="absolute inset-0 bg-gray-800"></div>
    {#if !mapReady}
      <div class="absolute inset-0 flex items-center justify-center bg-gray-900/50">
        <span class="text-white">Cargando mapa...</span>
      </div>
    {/if}
  </div>

  <div class="bg-white/5 border border-white/10 rounded-xl p-3 flex justify-center gap-6 text-sm flex-shrink-0">
    <div class="flex items-center gap-2">
      <div class="w-3 h-3 rounded-full bg-yellow-500"></div>
      <span class="text-gray-300">Pendiente</span>
    </div>
    <div class="flex items-center gap-2">
      <div class="w-3 h-3 rounded-full bg-green-500"></div>
      <span class="text-gray-300">Completada</span>
    </div>
    <div class="flex items-center gap-2">
      <div class="w-3 h-3 rounded-full bg-red-500"></div>
      <span class="text-gray-300">Fallida</span>
    </div>
  </div>
</div>
